import { Button, Stack } from '@mui/material';
import { Box } from '@mui/material';

import { useCallback, useContext, useEffect, useState } from 'react'

import { PhotoCamera } from '@material-ui/icons';
import { styled } from '@mui/system';
import ClientImagePreview from './ClientImagePreview';
import { nanoid } from 'nanoid';
import { putMultipartFormDataWithAuth } from '../../data/hikoFetch';
import { MahiContext } from '../state/MahiContext';
import { hikoException } from '../../utils/HikoAlert';
import { resizeFile } from '../../utils/ResizeImageFile';

const env = process.env.REACT_APP_ENV
const envConfig = require(`../../config/config.${env}.json`)

const StyledInput = styled('input')({
  display: 'none',
});

/**
 * 
 * @param {object} props { 
 *   bffPath {string}        - bffMahi relative path to endpoint that accepts PUT multipart-form-data and returns a {fileKey, url} JSON object
 *   initialFiles [<file>]   - list of File Objects (see files state variable below) which have already been uploaded.
 *                             Only used when component in constructed, never on re-render.
 *   fileTypes [<string>]    - list of file types accepted for upload
 *   statusChange {function} - parent supplied function which is called at various times during image upload. Two parameters are:
 *                             are a Boolean to say if the uploader is busy and
 *                             a list of upload file add/remove objects E.g.
 *                               [{action: 'Add', file: {filekey, url}}, {action: 'Remove', file: {filekey, url}}]
 * } 
 * @returns React component
 */
export default function MickUploadBox({
        bffPath,
        initialFiles = [],
        fileTypes = ['image/png', 'image/jpeg', 'application/pdf'],
        statusChange}) {

    const mahiContext = useContext(MahiContext)
    const maxConcurrentUploads = 2

    //
    // files is an array of File Objects e.g. [
    //   {
    //     file: <Javascript File Object>
    //     id: unique id generated by this uploader
    //     fileKey: "20220101.foo.jpg"
    //     url: "https://example.com/foo.jpg"
    //     status: 'Queued' | 'Uploading' | 'Error' | 'Uploaded'
    //   },
    //   ...
    // ]
    const [files, setFiles] = useState([])
  
    const emitStatusChangeCallback = useCallback(
        (allFiles, changeFileSpecs) => {

            // const debugFiles = (listOfFiles) => {
            //     listOfFiles.forEach((f, i) => {
            //         console.log(`File ${i} id`, f.id)
            //         console.log(`File ${i} fileKey`, f.fileKey)
            //         console.log(`File ${i} url`, f.url)
            //         console.log(`File ${i} status`, f.status)
            //     })
            // }

            const emitStatusChange = (allFiles, changeFileSpecs) => {
                // console.log('emitStatusChange allFiles', allFiles)
                // debugFiles(allFiles) 
                
                const isUploaderBusy = allFiles.filter((f) => ['Uploading'].includes(f.status)).length > 0
                statusChange(isUploaderBusy, changeFileSpecs)
            }

            emitStatusChange(allFiles, changeFileSpecs)
        },
        [statusChange],
    )

    useEffect(() => {
        //
        // Called once when we construct the uploader.
        // Gives us the chance to set up any files that were uploaded earlier.
        //
        const initialUploadedFiles = initialFiles?.filter((f) => f.status === 'Uploaded')
        setFiles(initialUploadedFiles)
    // eslint-disable-next-line
    }, [])

    useEffect(() => {
        //
        // This is the core of the uploader.
        // It is executed whenever files are added, removed
        // or have their status changed (e.g. upload complete).
        //
        const abortController = new AbortController()
        
        const uploadAsync = async (someFileObject) => {
            // console.log('uploadAsync', someFileObject)
            someFileObject.status = 'Uploading'

            const options = {}

            let resizeError = false
            if (someFileObject?.file?.type?.includes("image")) {
                try {
                    //
                    // This is an image so try to resize it.
                    //
                    const resizedFile = await resizeFile(
                        someFileObject.file,
                        envConfig.resizer.maxWidth,
                        envConfig.resizer.maxHeight,
                        'jpeg',
                        envConfig.resizer.quality,
                    )
                    someFileObject.file = resizedFile
                } catch (e) {
                    hikoException('Problem resizing your image file', e)
                    resizeError = true
                }
            }

            let uploadSucceeded = false
            if (!resizeError) {
                // console.log('uploadAsync about to putMultipartFormDataWithAuth', someFileObject)
                // console.log('uploadAsync bffPath', bffPath)
                // console.log('uploadAsync someFileObject.file', someFileObject.file)
                try {
                    const response = await putMultipartFormDataWithAuth(mahiContext, 'bffMahi', bffPath, someFileObject.file, options, abortController.signal)
                    if (response.fileKey && response.url) {
                        someFileObject.fileKey = response.fileKey
                        someFileObject.url = response.url
                        uploadSucceeded = true
                    }
                } catch (e) {
                    hikoException('Problem uploading image data to server', e)
                }
            }

            let fileChangeSpecs = []
            if (uploadSucceeded) {
                someFileObject.status = 'Uploaded'
                fileChangeSpecs.push({action: 'Add', file: someFileObject})
            } else {
                someFileObject.status = 'Error'
            }
            //
            // Set the files state variable again to ensure sub-components update with changed status.
            //
            setFiles([...files])
            emitStatusChangeCallback(files, fileChangeSpecs)
        }

        //
        // If there are no files we have nothing to upload.
        //
        if (!files) {
            return
        }

        //
        // See how many uploads are in progress.
        //
        const filesUploading = files.filter((f) => f.status === 'Uploading').length
        if (filesUploading < maxConcurrentUploads) {
            //
            // We have capacity to start a new upload.
            // Check to see if there are unprocessed files in the files list.
            //
            const filesQueued = files.filter((f) => f.status === 'Queued')
            const newUploadsAvailable = maxConcurrentUploads - filesUploading
            let newUploadsStarted = false
            for (let i = 0 ; i < filesQueued.length; i++) {
                if (i >= newUploadsAvailable) {
                    break
                }
                newUploadsStarted = true
                uploadAsync(filesQueued[i]) // run in background
            }
            if (newUploadsStarted) {
                //
                // Tell parent component that the uploader is busy.
                // The file change specs list is empty because we haven't completed any uploads.
                //
                emitStatusChangeCallback(files, [])
                //
                // Set the files state variable again to ensure sub-components update with changed status.
                //
                setFiles([...files])
            }
        }

    }, [files, bffPath, mahiContext, emitStatusChangeCallback])

    const handleInputFileChange = (e) => {
        // console.log('handleInputFileChange e.target.files', e.target.files)
        const fileList = e.target.files
        const fileArray = [...files]
        for (let i = 0; i < fileList.length; i++) {
            const newFile = fileList.item(i)
            const newFileObject = {
                file: newFile,
                id: nanoid(10),
                fileKey: newFile.name,
                url: null,
                status: 'Queued'
            }
            fileArray.push(newFileObject)
        }
        setFiles(fileArray)
    }

    const handleRemove = (id) => {
        console.log('handleRemove id', id)
        
        if (!files || !id) {
            console.error('Nothing to remove')
            return
        }

        const fileBeingRemoved = findFileById(files, id)
        if (!fileBeingRemoved) {
            console.error('Could not remove your file', id)
            return
        }
        const remainingFiles = removeFileById(files, id)
        setFiles(remainingFiles)
        emitStatusChangeCallback(remainingFiles, [{action: 'Remove', file: fileBeingRemoved}])
    }

    const findFileById = (fileList, id) => {
        return fileList.find((f) => f.id === id)
    }

    const removeFileById = (fileList, id) => {
        return fileList.filter((f) => f.id !== id)
    }

    return (
        <Box>
            <Box>
                <label htmlFor="file-upload-input">
                    <StyledInput
                        // accept="image/png, image/jpeg, application/pdf"
                        accept={fileTypes}
                        id="file-upload-input"
                        onChange={handleInputFileChange}
                        multiple
                        type="file"
                    />
                    <Button variant="contained" component="span" endIcon={<PhotoCamera />}> 
                        Choose Files
                    </Button>
                </label>
            </Box>
            <Box>
                <Stack direction="row" sx={{flexWrap: 'wrap', my: 2}}>
                    {files && files.map((f) => {
                        return (
                            <ClientImagePreview
                                key={f.id}
                                file={f.file}
                                status={f.status}
                                size={180}
                                id={f.id}
                                remove={handleRemove}
                                sx={{mb: 2, mr: 2}}
                            />
                        )
                    })
                    }
                </Stack>
            </Box>
            {/* <DebugBox title="files">
                <List>
                    {files && files.map((f) => {
                        return (
                            <ListItem key={f.id}>
                                name: {f.file.name}<br />
                                size: {f.file.size}<br />
                                type: {f.file.type}<br />
                            </ListItem>
                        )
                    })}
                </List>
            </DebugBox> */}
        </Box>
    )
}

